/**
 * THE SHARED PROGRESS MINI — the bar + % + health dot, used identically on the
 * card face and in the grid so the two can never drift (one product). It reads a
 * `ProgressHealth` straight off progress-health.ts: the fill shows how far, the dot
 * takes the health tone, and the faint tick marks what the calendar expects by now
 * (so "behind" reads as fill-short-of-the-tick even with colour off). Renders
 * nothing when there is no % to show.
 */
import type { ProgressHealth } from '../progress-health';
import type { ProgressSource } from '../progress-derive';
import { DataBar } from './DataBar';
import type { BarMarker } from './DataBar';

const toneClass = (h: ProgressHealth): string =>
  h.tone === 'good' ? 'is-good' : h.tone === 'warn' ? 'is-warn' : h.tone === 'bad' ? 'is-bad' : 'is-tracking';

/** The DataBar fill tone for a progress health — the 'tracking' state (no due date, so no
 *  behind/ahead reading) draws neutral. */
const fillTone = (h: ProgressHealth): 'good' | 'warn' | 'bad' | 'neutral' =>
  h.tone === 'good' ? 'good' : h.tone === 'warn' ? 'warn' : h.tone === 'bad' ? 'bad' : 'neutral';

/** The one-line description for the title/aria-label — value paired with its read. */
export const progressTitle = (h: ProgressHealth): string =>
  h.kind === 'tracking'
    ? `${h.pct}% done — no due date set`
    : h.reason
      ? `${h.label} — ${h.reason}`
      : h.label || `${h.pct}% done`;

/** The provenance suffix for a derived %. Vinnie, 27 Oct: a checklist-derived % earns
 *  a suffix naming the COUNT ("N of M") rather than the words "from the checklist"; a
 *  % from the board DoD alone, or a typed number, says nothing because the figure
 *  speaks for itself. A COMBINED % (card checklist + DoD pooled, Vinnie 3 Nov) earns
 *  it too — the count is the pooled done/total. The count needs the done/total, so the
 *  caller passes `checklistCount`; without it there is nothing to name and it drops. */
const suffixOf = (
  source: ProgressSource,
  count?: { done: number; total: number } | null,
): string | null =>
  (source === 'card-checklist' || source === 'combined') && count ? `${count.done} of ${count.total}` : null;

export function ProgressMini({
  health,
  showPct = true,
  showDot = true,
  source,
  checklistCount,
  className = '',
}: {
  health: ProgressHealth;
  showPct?: boolean;
  showDot?: boolean;
  /** Where the shown % came from. 'card-checklist' and 'combined' are named by their
   *  count (see `checklistCount`); 'done-checks', 'manual' and null add nothing — the
   *  figure speaks for itself (Vinnie, 27 Oct / 3 Nov). */
  source?: ProgressSource;
  /** The tally behind a derived %, so it can read "N of M" — the checklist's own for
   *  'card-checklist', the pooled checklist+DoD for 'combined'. Absent elsewhere. */
  checklistCount?: { done: number; total: number } | null;
  className?: string;
}) {
  if (health.kind === 'none' || health.pct == null) return null;
  const suffix = suffixOf(source ?? null, checklistCount);
  const title = suffix ? `${progressTitle(health)} — ${suffix}` : progressTitle(health);
  const showTick = health.expectedPct != null && health.kind !== 'tracking';
  // The "where the calendar expects you by now" tick is the shared capped marker on a wide
  // hover lane — hoverable and readable, not a 2px hairline. Its number lives in the hover
  // (the card face is too tight for an inline figure); the tick's POSITION reads at a glance.
  const gap = showTick ? health.pct - health.expectedPct! : 0;
  const markers: BarMarker[] = showTick
    ? [{
        pct: health.expectedPct!,
        soft: true,
        tip: {
          title: `Plan: ${health.expectedPct}% by today`,
          what: (
            <>On today's plan you'd be at <b>{health.expectedPct}%</b>.{' '}
              {gap < 0 ? <>You're <b>{-gap} points behind</b>.</> : gap > 0 ? <>You're <b>{gap} points ahead</b>.</> : <>You're exactly on plan.</>}</>
          ),
        },
      }]
    : [];
  return (
    <span className={`prog-mini ${toneClass(health)}${className ? ' ' + className : ''}`} role="img" aria-label={title}>
      {showDot && <span className="prog-dot" />}
      <DataBar density="tile" className="prog-dbar" fill={{ to: health.pct, tone: fillTone(health) }} markers={markers} />
      {showPct && <span className="prog-pct">{health.pct}%</span>}
      {suffix && <span className="prog-src">· {suffix}</span>}
    </span>
  );
}
